import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import loadable from '@loadable/component'
import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom'
import ComponentSelector from '../../ComponentSelector'
import Footer from '../../../containers/Footer/index'
import PageTemplate from '../../templates/PageTemplate'
import { venture, ventures, baseUrl, isNewHeader } from '../../../config'
import breadcrumbSchema from '../../../services/jsonld/jsonldSchema'
import { updateBingoLobbyFeedData } from '../../../store/actions'
import { constructFAQSchema } from '../../../services/jsonld/faqSchema'
import extrapolateVideoSchemas from '../../../services/jsonld/videoSchema'
import useCometD from './hooks/useCometD'
import useGlobalPrivacyControl from './hooks/useGlobalPrivacyControl'

const DynamicHeader = loadable(() =>
  import('@albatross/common-components/dist/components/DynamicHeader')
)
const HTMLHeaderElements = loadable(() => import('@albatross/common-components/dist/components/MetaTags'))
const NavigationBreadcrumb = loadable(() => import('@albatross/common-components/dist/components/NavigationBreadcrumb'))

const RemoveMargin = styled.div`
  width: 100%;
  margin: -48px;
`

const LandingPage = (props) => {
  const {
    title,
    canonical,
    metaTags,
    components,
    globalContent,
    customTermsAndConditions,
    hasMargin,
    jsonld,
    indexed,
    path,
    globalComponents,
    pageFooterOverride,
    overrideComponents,
    updateBingoLobbyFeedData,
    ConfigContxt,
    disableBreadcrumb,
    enableContentful,
    brand
  } = props

  useGlobalPrivacyControl(ventures[venture]?.gpcFeatureEnabled)
  useCometD((data) => updateBingoLobbyFeedData(data))
  const schemaValues = { url: `${baseUrl}${path}`, base: baseUrl, categories: [] }
  const jsonldArray = [
    jsonld,
    breadcrumbSchema(schemaValues),
    ...extrapolateVideoSchemas(components)
  ].filter((x) => x)

  if (props.jsonldFAQ?.hasJsonldFAQ) {
    jsonldArray.push(constructFAQSchema(props.jsonldFAQ.arrayOfJsonldFAQ))
  }
  const location = useLocation()
  const hasSubNav = !!components.filter(component => component.type === 'NavigationMenu' || component.type === 'UnicornNavigationMenu').length
  const isThreeLevelsDeep = location.pathname.split('/').length > 3
  const navigationMenuCategories = components?.filter(({ type }) => type === 'NavigationMenu')[0]?.props?.categories ?? []
  const navigationBreadcrumbsProps = {
    showBreadcrumb: !disableBreadcrumb && globalContent.showBreadcrumb,
    venture,
    navigationMenuCategories
  }

  return (
    <PageTemplate
      components={components}
      globalContent={globalContent}
    >
      {hasMargin && <RemoveMargin />}
      <HTMLHeaderElements
        title={title}
        canonical={canonical || `${baseUrl}${path}`}
        metaTags={metaTags}
        jsonld={jsonldArray}
        indexed={indexed}
        headerLang={globalContent.headerLang}
        currentPathname={location.pathname}
      />
      <DynamicHeader
        venture={venture}
        globalConfig={globalComponents?.components?.DynamicHeader?.globalConfig}
        pageConfig={{
          ...overrideComponents?.header,
          ...(isNewHeader && { showNewHeader: true })
        }}
        categories={globalContent.menuItems}
        hasSubNav={hasSubNav}
        appLink={globalContent.appLinks}
        dropdownsList={globalContent.dropdownMenu}
        enableContentful={enableContentful}
      />
      { isThreeLevelsDeep && <NavigationBreadcrumb {...navigationBreadcrumbsProps} />}
      <ComponentSelector {...{ components, globalContent, globalComponents, ConfigContxt, enableContentful, brand }} />
      {!isThreeLevelsDeep && <NavigationBreadcrumb {...navigationBreadcrumbsProps} />}
      <Footer
        customTermsAndConditions={customTermsAndConditions}
        venture={venture}
        pageFooterOverride={pageFooterOverride}
        lang={globalContent.lang && globalContent.lang}
        enableContentful={enableContentful}
      />
    </PageTemplate>
  )
}
LandingPage.propTypes = {
  title: PropTypes.string.isRequired,
  path: PropTypes.string,
  canonical: PropTypes.string,
  globalContent: PropTypes.object,
  ConfigContxt: PropTypes.object,
  globalComponents: PropTypes.object,
  pageFooterOverride: PropTypes.object,
  customTermsAndConditions: PropTypes.string,
  hasMargin: PropTypes.bool,
  jsonld: PropTypes.string,
  jsonldFAQ: PropTypes.object,
  indexed: PropTypes.bool,
  brand: PropTypes.object,
  metaTags: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired
    })
  ),
  components: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      props: PropTypes.object
    })
  ),
  overrideComponents: PropTypes.object,
  updateBingoLobbyFeedData: PropTypes.func,
  disableBreadcrumb: PropTypes.bool,
  enableContentful: PropTypes.bool
}

const mapDispatchToProps = (dispatch) => ({
  updateBingoLobbyFeedData: (data) => dispatch(updateBingoLobbyFeedData(data))
})

export default connect(null, mapDispatchToProps)(LandingPage)
